import { Site, SiteFeatureFlag, TestValues } from "../resources/modelTypes";
import { useCurrentSite } from "./models/SiteContext";
import { useMediaQueryState } from "@minted/minted-components";

/**
 * Hook to check if the reworked OI guest dashboard is enabled for this site.
 * The flag value is captured at site creation time and stored in feature_flags.
 */
export const useOIReworkedDashboardEnabled = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.oiDashboardV2] === TestValues.Experiment
  );
};

/**
 * Whether the new home page layout experience is enabled for a site.
 *
 * The Layout and Fonts & Colors views this unlocks are only reachable from the
 * wedding sidebar, so the flag is inert for other site types.
 */
export const isNewLayoutsEnabled = (site: Site): boolean =>
  site.isWedding &&
  site.featureFlags[SiteFeatureFlag.NewLayouts] === TestValues.Experiment;

/**
 * Hook to check if the new home page layout experience is enabled for this site.
 */
export const useNewLayouts = (): boolean => {
  const site = useCurrentSite();
  return isNewLayoutsEnabled(site);
};

/**
 * Custom hooks to get experience flags.
 * They must be used inside a LaunchDarklyProvider.
 */
export const useNewMobileExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  return mediumMediaQueryState === "BELOW" && site.isOnlineInvitation;
};

export const useNewDesktopExperience = (): boolean => {
  const site = useCurrentSite();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });

  if (!site.isOnlineInvitation) {
    return false;
  }

  if (mediumMediaQueryState === "BELOW") {
    return false;
  }

  return (
    site.featureFlags[SiteFeatureFlag.SectionEditorExperience] ===
    TestValues.Experiment
  );
};

export const useGuestHouseholdMembersFlag = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.ConnectHouseholdMembers] ===
    TestValues.Experiment
  );
};

export const useStripeRegistries = (): boolean => {
  const site = useCurrentSite();
  return (
    site.featureFlags[SiteFeatureFlag.StripeRegistry] === TestValues.Experiment
  );
};
